/**
 * Spec Phase 4 Slice 4E — generic Dock wrapper.
 *
 * `<details>`-based collapsible dock per brief §4. Header shows
 * chevron + icon + title + optional pill (count) + optional meta
 * line. Open/closed state is mirrored to the Zustand
 * docksOpen map (sticky via localStorage) by the parent rail.
 */
import React from 'react';
import { ChevronDown } from 'lucide-react';
import { useSpecStore, type DockKey } from '@/store/specStore';

interface Props {
  dockKey: DockKey;
  icon?: React.ReactNode;
  title: string;
  pillCount?: number;
  meta?: string;
  testId?: string;
  children: React.ReactNode;
}

export default function Dock({
  dockKey,
  icon,
  title,
  pillCount,
  meta,
  testId,
  children,
}: Props): JSX.Element {
  const open = useSpecStore((s) => s.docksOpen[dockKey]);
  const toggleDock = useSpecStore((s) => s.toggleDock);

  return (
    <details
      open={open}
      className="border-b border-zinc-200"
      data-testid={testId}
    >
      <summary
        className="flex cursor-pointer list-none items-center gap-2 px-4 py-2 hover:bg-zinc-50"
        onClick={(e) => {
          // Pre-empt the native toggle so the store stays the
          // source of truth (and persistence fires).
          e.preventDefault();
          toggleDock(dockKey);
        }}
      >
        <ChevronDown
          className={[
            'h-3.5 w-3.5 text-zinc-400 transition-transform',
            open ? '' : '-rotate-90',
          ].join(' ')}
          aria-hidden
        />
        {icon}
        <span className="text-xs font-medium text-zinc-800">{title}</span>
        {typeof pillCount === 'number' && (
          <span
            className="ml-auto rounded-sm bg-zinc-100 px-1.5 py-0.5 font-mono text-[10px] text-zinc-600"
            data-testid={`${testId}-pill`}
          >
            {pillCount}
          </span>
        )}
      </summary>
      {meta && (
        <div className="px-4 pb-2 text-[11px] text-zinc-500">{meta}</div>
      )}
      {/* BUG-035 — Each dock caps its expanded content at 40vh and
          scrolls internally. Without this a long Frozen-claims dock
          (16+ cards) pushed the others off-screen even with the
          rail's outer overflow. */}
      <div
        className="px-4 pb-3 overflow-y-auto"
        style={{ maxHeight: '40vh' }}
      >
        {children}
      </div>
    </details>
  );
}
